import React, { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const SLOTS = ["Any time", "Morning (9am–12pm)", "Afternoon (12pm–4pm)", "Evening (4pm–7pm)"];

export function CallbackForm() {
  const { user } = useAuth();
  const [mobile, setMobile] = useState(user?.mobile || "");
  const [slot, setSlot] = useState("Any time");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(mobile.replace(/\D/g, "").slice(-10)) || mobile.replace(/\D/g, "").length < 10) { toast.error("Enter a valid 10-digit mobile number."); return; }
    setLoading(true);
    try {
      await api.post("/callbacks", { mobile: mobile.trim(), preferred_time: slot, reason: reason.trim() });
      setDone(true);
      toast.success("Callback requested — our team will reach out.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div data-testid="callback-form-done" className="w-full max-w-sm rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800">
        📞 Thanks! Our team will call you on <b>{mobile}</b>{slot !== "Any time" ? ` during ${slot}` : ""}. We'll be in touch soon.
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="callback-form"
      className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-5 shadow-sm space-y-3">
      <p className="font-heading font-semibold text-emerald-950">Request a callback</p>
      <div className="space-y-1.5">
        <Label className="text-xs">Mobile number</Label>
        <Input data-testid="callback-mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="9886260579" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Preferred time</Label>
        <Select value={slot} onValueChange={setSlot}>
          <SelectTrigger data-testid="callback-slot"><SelectValue /></SelectTrigger>
          <SelectContent>
            {SLOTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">What's it about? <span className="text-zinc-400">(optional)</span></Label>
        <Textarea data-testid="callback-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Help with a cashless claim" />
      </div>
      <Button type="submit" disabled={loading} data-testid="callback-submit"
        className="w-full rounded-full bg-emerald-900 hover:bg-emerald-800 text-white transition-colors">
        {loading ? "Requesting…" : "Request callback"}
      </Button>
    </form>
  );
}
